import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Play, 
  Pause, 
  RotateCcw, 
  ArrowRight, 
  ArrowLeft,
  CheckCircle,
  FileText,
  Clock,
  Download,
  Star,
  Heart,
  Users,
  Building,
  Shield,
  CreditCard,
  Sparkles
} from "lucide-react";

const InteractiveDemo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [demoAnswers, setDemoAnswers] = useState({});
  const [autoAdvance, setAutoAdvance] = useState(false);

  const demoSteps = [
    {
      id: "welcome",
      title: "Welcome to FormForgePro",
      description: "Your AI-powered legal assistant for comprehensive document preparation",
      duration: 3000,
      component: "Welcome"
    },
    {
      id: "service-selection",
      title: "Choose Your Legal Service",
      description: "Browse our comprehensive catalog of legal document services",
      duration: 4000,
      component: "ServiceSelection"
    },
    {
      id: "qualification",
      title: "Quick Qualification Check",
      description: "Answer a few questions to ensure this service is right for you",
      duration: 5000,
      component: "Qualification"
    },
    {
      id: "payment",
      title: "Secure Payment",
      description: "Complete your purchase with our secure payment system",
      duration: 3000,
      component: "Payment"
    },
    {
      id: "questionnaire",
      title: "Complete Your Forms",
      description: "Fill out your legal documents with our guided questionnaire",
      duration: 6000,
      component: "Questionnaire"
    },
    {
      id: "generation",
      title: "Document Generation",
      description: "Our AI generates your customized legal documents",
      duration: 4000,
      component: "Generation"
    },
    {
      id: "completion",
      title: "Download & File",
      description: "Get your completed documents with filing instructions",
      duration: 3000,
      component: "Completion"
    }
  ];

  const sampleService = {
    id: 1,
    title: "Simple Will",
    description: "Basic will document with standard provisions for asset distribution",
    price: 79.00,
    duration: "15-20 minutes",
    category: "Wills, Estates & Probate",
    features: ["Last Will & Testament", "Witness Requirements", "Notarization Guide", "Storage Instructions"]
  };

  useEffect(() => {
    if (isPlaying && autoAdvance) {
      const timer = setTimeout(() => {
        if (currentStep < demoSteps.length - 1) {
          setCurrentStep(currentStep + 1);
          setProgress(((currentStep + 1) / demoSteps.length) * 100);
        } else {
          setIsPlaying(false);
          setAutoAdvance(false);
        }
      }, demoSteps[currentStep].duration);

      return () => clearTimeout(timer);
    }
  }, [isPlaying, currentStep, autoAdvance, demoSteps]);

  const handlePlay = () => {
    setIsPlaying(true);
    setAutoAdvance(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
    setAutoAdvance(false);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setAutoAdvance(false);
    setCurrentStep(0);
    setProgress(0);
    setDemoAnswers({});
  };

  const handleNext = () => {
    if (currentStep < demoSteps.length - 1) {
      setCurrentStep(currentStep + 1);
      setProgress(((currentStep + 1) / demoSteps.length) * 100);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setProgress(((currentStep - 1) / demoSteps.length) * 100);
    }
  };

  const renderStepContent = () => {
    const step = demoSteps[currentStep];
    
    switch (step.component) {
      case "Welcome":
        return (
          <div className="text-center space-y-6">
            <div className="text-6xl font-bold">
              <span className="text-teal-600">Form</span>
              <span className="text-teal-800">Forge</span>
              <span className="text-teal-600">Pro</span>
            </div>
            <p className="text-xl text-gray-600">
              Your AI-powered legal assistant for trusts, wills, incorporations, and legal guidance
            </p>
            <div className="flex justify-center gap-4 text-sm">
              <Badge className="bg-yellow-100 text-yellow-800">
                <Sparkles className="w-4 h-4 mr-1" />
                Enhanced with Real Legal Data
              </Badge>
              <Badge className="bg-yellow-100 text-yellow-800">
                Free Government APIs
              </Badge>
              <Badge className="bg-yellow-100 text-yellow-800">
                AI Document Analysis
              </Badge>
            </div>
          </div>
        );

      case "ServiceSelection":
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800">Browse Legal Services</h3>
              <p className="text-gray-600">Select from our comprehensive catalog of legal documents</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-yellow-200 bg-yellow-50">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <FileText className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800">{sampleService.title}</h4>
                      <p className="text-sm text-gray-600">{sampleService.description}</p>
                      <div className="flex items-center mt-2 text-sm text-gray-500">
                        <Clock className="w-4 h-4 mr-1" />
                        {sampleService.duration}
                        <span className="ml-3">${sampleService.price}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow cursor-pointer opacity-50">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Building className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-600">LLC Formation</h4>
                      <p className="text-sm text-gray-500">Form your Limited Liability Company</p>
                      <div className="flex items-center mt-2 text-sm text-gray-400">
                        <Clock className="w-4 h-4 mr-1" />
                        25-30 minutes
                        <span className="ml-3">$149</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="text-center">
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle className="w-4 h-4 mr-1" />
                Selected: {sampleService.title}
              </Badge>
            </div>
          </div>
        );

      case "Qualification":
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800">Quick Qualification Check</h3>
              <p className="text-gray-600">Help us ensure our service is the right fit for your needs</p>
            </div>

            <Card className="border-yellow-200">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-800 mb-3">
                      Do you have a clear understanding of your legal issue?
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                        <span className="text-gray-700">Yes, I know exactly what I need</span>
                      </div>
                      <div className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg opacity-50">
                        <div className="w-4 h-4 border-2 border-gray-300 rounded-full"></div>
                        <span className="text-gray-500">I have a general idea</span>
                      </div>
                      <div className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg opacity-50">
                        <div className="w-4 h-4 border-2 border-gray-300 rounded-full"></div>
                        <span className="text-gray-500">I'm not sure what I need</span>  
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="text-center">
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle className="w-4 h-4 mr-1" />
                Qualification: Passed ✓
              </Badge>
            </div>
          </div>
        );

      case "Payment":
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800">Secure Payment</h3>
              <p className="text-gray-600">Complete your purchase with bank-level security</p>
            </div>

            <Card className="max-w-md mx-auto">
              <CardHeader>
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{sampleService.title}</span>
                  <span className="font-semibold">${sampleService.price}</span>
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center text-lg font-semibold">
                    <span>Total</span>
                    <span>${sampleService.price}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center">
                      <CreditCard className="w-5 h-5 text-blue-600 mr-2" />
                      <span className="text-blue-800 font-medium">Secure Payment via Stripe</span>
                    </div>
                  </div>
                  
                  <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold">
                    <Shield className="w-4 h-4 mr-2" />
                    Complete Payment
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="text-center text-sm text-gray-500">
              💡 In the real process, you'll be redirected to Stripe's secure payment page
            </div>
          </div>
        );

      case "Questionnaire":
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800">Complete Your Legal Forms</h3>
              <p className="text-gray-600">Answer guided questions to customize your documents</p>
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="mb-6">
                <Progress value={60} className="h-2" />
                <p className="text-sm text-gray-500 mt-2">Question 3 of 5</p>
              </div>

              <Card>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-800">
                      What is your full legal name?
                    </h4>
                    <Input 
                      value="John Smith" 
                      className="bg-yellow-50 border-yellow-200"
                      readOnly 
                    />
                    <p className="text-sm text-gray-500">
                      Enter your name exactly as it appears on official documents
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="mt-4">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-800">
                      In which state do you need this document?
                    </h4>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <span className="text-gray-700">California</span>
                    </div>
                    <p className="text-sm text-gray-500">
                      Select your state for state-specific requirements
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case "Generation":
        return (
          <div className="space-y-6 text-center">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-800">Generating Your Documents</h3>
              <p className="text-gray-600">Our AI is creating your customized legal documents</p>
            </div>

            <div className="max-w-md mx-auto">
              <div className="relative">
                <div className="animate-spin rounded-full h-24 w-24 border-b-2 border-yellow-500 mx-auto"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-yellow-600" />
                </div>
              </div>
              
              <div className="mt-6 space-y-2">
                <div className="flex items-center justify-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  Analyzing your responses
                </div>
                <div className="flex items-center justify-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  Applying California state laws
                </div>
                <div className="flex items-center justify-center text-sm text-yellow-600">
                  <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Generating PDF documents
                </div>
                <div className="flex items-center justify-center text-sm text-gray-400">
                  <div className="w-4 h-4 border-2 border-gray-300 rounded-full mr-2"></div>
                  Preparing filing instructions
                </div>
              </div>
            </div>
          </div>
        );

      case "Completion":
        return (
          <div className="space-y-6 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Documents Ready!</h3>
              <p className="text-gray-600">Your legal documents have been generated and are ready for download</p>
            </div>

            <Card className="max-w-md mx-auto">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="text-left">
                    <h4 className="font-semibold text-gray-800 mb-3">Your Documents:</h4>
                    <ul className="space-y-2">
                      {sampleService.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-700">
                          <FileText className="w-4 h-4 text-green-500 mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold">
                      <Download className="w-4 h-4 mr-2" />
                      Download All Documents
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="text-sm text-gray-500">
              ✨ Process completed in under 2 minutes! 
              <br />
              Documents have been emailed to you as well.
            </div>
          </div>
        );

      default:
        return <div>Step content not found</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-yellow-100 text-yellow-800">
            Interactive Demo
          </Badge>
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            See FormForgePro In Action
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Watch how our AI creates a complete legal document in under 2 minutes, 
            saving you thousands in legal fees.
          </p>
        </div>

        {/* Demo Controls */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <Button
                onClick={isPlaying ? handlePause : handlePlay}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                {isPlaying ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                {isPlaying ? 'Pause' : 'Play'}
              </Button>
              <Button variant="outline" onClick={handleReset}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleNext}
                disabled={currentStep === demoSteps.length - 1}
              >
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="mb-4">
            <Progress value={progress} className="h-2" />
            <div className="flex justify-between text-sm text-gray-500 mt-2">
              <span>Step {currentStep + 1} of {demoSteps.length}</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              {demoSteps[currentStep].title}
            </h2>
            <p className="text-gray-600">
              {demoSteps[currentStep].description}
            </p>
          </div>
        </div>

        {/* Demo Content */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 min-h-[500px]">
          {renderStepContent()}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            Ready to Get Started?
          </h3>
          <p className="text-gray-600 mb-6">
            Experience the full power of FormForgePro for your legal document needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">

            <Button 
              variant="outline"
              size="lg"
              className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-3"
            >
              Browse Services
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveDemo;